import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';

export type ParamMap = Record<string, string>;

type Ctx = {
    ready: boolean;
    params: ParamMap;
    refresh: () => Promise<void>;
    // helpers
    getNumber: (key: string, fallback: number) => number;
    getString: (key: string, fallback: string) => string;
    getJson: <T>(key: string, fallback: T) => T;
    getWorkingDays: (fallback?: number[]) => number[];
};

const ParametersContext = createContext<Ctx | undefined>(undefined);

const PARAM_KEYS = {
    lessonsPerDay: 'lessons.per.day',
    teacherWorkingDays: 'teacher.working.days',
    defaultLessonDuration: 'default.lesson.duration.minutes',
    defaultLessonType: 'default.lesson.type',
};

// Load public (non-portal) parameters from a local static config bundled with the frontend
// This removes the need to store public parameters in the backend DB.
import publicParams from '../config/publicParams.json';

async function fetchPublic(): Promise<ParamMap> {
    // The JSON file already conforms to a simple map { key: value }
    // Keep the function async to preserve the existing call sites and API.
    try {
        return (publicParams as unknown as Record<string, string>) || {};
    } catch {
        return {} as ParamMap;
    }
}

async function fetchPrivate(): Promise<ParamMap> {
    // Authenticated users can read all (public+private) as a map
    const map = await apiService.get<Record<string, string>>(`/api/parameters/portal`);
    return map || {};
}

export const ParametersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [ready, setReady] = useState(false);
    const [params, setParams] = useState<ParamMap>({});

    const load = useCallback(async () => {
        try {
            const pub = await fetchPublic();
            let merged = { ...pub } as ParamMap;
            // Only attempt to load private parameters if we have evidence of an authenticated session
            let shouldFetchPrivate = false;
            try {
                if (typeof window !== 'undefined') {
                    shouldFetchPrivate = localStorage.getItem('ud_isAuthenticated') === 'true';
                }
            } catch (_) {}
            if (shouldFetchPrivate) {
                try {
                    const priv = await fetchPrivate();
                    merged = { ...merged, ...priv };
                } catch (_) {
                    // Not authenticated or endpoint not available — keep public only
                }
            }
            setParams(merged);
        } catch (e) {
            // swallow; keep defaults
            // console.warn('[ParametersProvider] failed to load parameters', e);
        } finally {
            setReady(true);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const getString = useCallback((key: string, fallback: string) => {
        const v = params[key];
        if (typeof v !== 'string') return fallback;
        return v;
    }, [params]);

    const getNumber = useCallback((key: string, fallback: number) => {
        const v = params[key];
        if (v == null) return fallback;
        const n = Number(v);
        return Number.isFinite(n) ? n : fallback;
    }, [params]);

    const getJson = useCallback(<T,>(key: string, fallback: T): T => {
        const v = params[key];
        if (!v) return fallback;
        try { return JSON.parse(v) as T; } catch { return fallback; }
    }, [params]);

    const getWorkingDays = useCallback((fallback: number[] = [1,2,3,4,5]) => {
        return getJson<number[]>(PARAM_KEYS.teacherWorkingDays, fallback);
    }, [getJson]);

    const value = useMemo<Ctx>(() => ({
        ready,
        params,
        refresh: load,
        getNumber,
        getString,
        getJson,
        getWorkingDays,
    }), [ready, params, load, getNumber, getString, getJson, getWorkingDays]);

    return (
        <ParametersContext.Provider value={value}>
            {children}
        </ParametersContext.Provider>
    );
};

export function useSystemParameters(): Ctx {
    const ctx = useContext(ParametersContext);
    if (!ctx) {
        // In tests, provide a safe default context so components can render without explicitly wrapping
        // with ParametersProvider. This avoids brittle tests while keeping production strict.
        if (process.env.NODE_ENV === 'test') {
            const testCtx: Ctx = {
                ready: true,
                params: {},
                refresh: async () => {},
                getNumber: (_key: string, fallback: number) => fallback,
                getString: (_key: string, fallback: string) => fallback,
                getJson: <T,>(_key: string, fallback: T) => fallback,
                getWorkingDays: (fallback: number[] = [1, 2, 3, 4, 5]) => fallback,
            };
            return testCtx;
        }
        throw new Error('useSystemParameters must be used within ParametersProvider');
    }
    return ctx;
}

export { PARAM_KEYS };
